// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Color } from '../../../Graphics';
import { PriceType, PriceTypeMap } from '@shared/utils/History/CashItemUtils';
import { MAMode, RSIMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterSeparator } from '../InputParamaterClasses/InputParameterSeparator';
export class RSI extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Relative Strength Index';
        this.SetIndicatorLine('Line 1', Color.Green, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('Line 2', Color.SkyBlue, 1, LineStyle.SimpleChart);
        this.SetLevelLine('Line 3', 70, Color.Red, 1, LineStyle.SimpleChart);
        this.SetLevelLine('Line 4', 30, Color.CreateColor(0, 51, 252), 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.SourcePrice = PriceType.Close;
        super.InputParameter(new InputParameterCombobox('SourcePrice', 'Source prices for RSI', 0, [
            { Close: PriceType.Close },
            { Open: PriceType.Open },
            { High: PriceType.High },
            { Low: PriceType.Low },
            { Typical: PriceType.Typical },
            { Medium: PriceType.Medium },
            { Weighted: PriceType.Weighted }
        ]));
        this.RSIPeriod = 14;
        super.InputParameter(new InputParameterInteger('RSIPeriod', 'RSI Period:', 0, 1, 100, 0, 1));
        this.RSIMethod = RSIMode.Simple;
        super.InputParameter(new InputParameterCombobox('RSIMethod', 'RSI Method:', 1, [
            { Simple: RSIMode.Simple },
            { Exponential: RSIMode.Exponential },
            { 'Wilder`s moving average': RSIMode.WMA }
        ]));
        super.InputParameter(new InputParameterSeparator(undefined, 'Moving average of RSI', 2));
        this.MAPeriod = 5;
        super.InputParameter(new InputParameterInteger('MAPeriod', 'Period of Moving Average', 2, 1, 9999, undefined, 1));
        this.MaType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MaType', 'Type of moving average', 3, [
            { Simple: MAMode.SMA },
            { Exponential: MAMode.EMA },
            { Modified: MAMode.SMMA },
            { 'Linear Weighted': MAMode.LWMA }
        ]));
        this.previousUp = 0;
        this.previousDown = 0;
        this.currentUp = 0;
        this.currentDown = 0;
        this.CalcHandlers = {};
        this.CalcHandlers[RSIMode.Simple] = this.CalcSimple.bind(this);
        this.CalcHandlers[RSIMode.WMA] = this.CalcWMA.bind(this);
        this.CalcHandlers[RSIMode.Exponential] = this.CalcExpon.bind(this);
    }
    GetIndicatorShortName() {
        return 'RSI(' + this.RSIPeriod + ',' + this.MAPeriod + ';' + PriceTypeMap[this.SourcePrice].toLowerCase() + ')';
    }
    getCustomName() {
        return 'RSI (' + this.ProjectName + ')';
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.UpdateIndexDrawBegin();
    }
    UpdateIndexDrawBegin() {
        this.SetIndexDrawBegin(0, this.RSIPeriod);
        this.SetIndexDrawBegin(1, this.RSIPeriod + this.MAPeriod);
    }
    OnQuote() {
        super.OnQuote();
        if (this.CurrentData.Count <= this.RSIPeriod || this.CurrentData.Count <= this.MAPeriod) {
            this.SetValue(0, 0, 0);
            this.SetValue(1, 0, 0);
            return;
        }
        this.CalcHandlers[this.RSIMethod]();
        if (this.CurrentData.Count <= this.RSIPeriod + this.MAPeriod) {
            this.SetValue(1, 0, 0);
            return;
        }
        const maValue = this.rsiMa.GetValue();
        this.SetValue(1, 0, maValue);
    }
    NextBar() {
        this.previousUp = this.currentUp;
        this.previousDown = this.currentDown;
        super.NextBar();
    }
    createInternalIndicators() {
        const rsiAccessor = (offset) => {
            const value = this.CurrentData.Count > offset + 1 ? this.GetValue(0, offset + 1) : this.GetValue(0, offset);
            return value == Number.MAX_VALUE ? 0 : value;
        };
        if (this.MaType === MAMode.SMA || this.MaType === MAMode.LWMA) {
            this.rsiMa = this.Indicators.iMAOnArray(this.CurrentData, rsiAccessor, this.MAPeriod, this.MaType);
        }
        else {
            this.rsiMa = this.Indicators.iMAExOnArray(this.CurrentData, rsiAccessor, this.MAPeriod, this.MaType, 0, 1);
        }
        return [this.rsiMa];
    }
    CalcSimple() {
        const RSIPeriod = this.RSIPeriod;
        const CurrentData = this.CurrentData;
        this.currentUp = 0;
        this.currentDown = 0;
        for (let i = 0; i < RSIPeriod; i++) {
            const diff = CurrentData.getPriceByType(this.SourcePrice, i) - CurrentData.getPriceByType(this.SourcePrice, i + 1);
            if (diff > 0) {
                this.currentUp += diff;
            }
            else {
                this.currentDown += Math.abs(diff);
            }
        }
        const rsi = this.currentDown != 0 ? 100.0 * (1.0 - 1.0 / (1.0 + this.currentUp / this.currentDown)) : 100;
        this.SetValue(0, 0, rsi);
    }
    CalcExpon() {
        const currentData = this.CurrentData;
        const N = this.RSIPeriod;
        if (currentData.Count === N + 1) {
            this.CalcSimple();
            this.currentUp = this.currentUp / N;
            this.previousUp = this.currentUp;
            this.currentDown = this.currentDown / N;
            this.previousDown = this.currentDown;
        }
        else {
            const close = currentData.getPriceByType(this.SourcePrice, 0);
            const prevClose = currentData.getPriceByType(this.SourcePrice, 1);
            const diff = close - prevClose;
            if (diff > 0) {
                this.currentUp = (this.previousUp * (N - 1) + diff) / N;
                this.currentDown = (this.previousDown * (N - 1)) / N;
            }
            else {
                this.currentUp = (this.previousUp * (N - 1)) / N;
                this.currentDown = (this.previousDown * (N - 1) - diff) / N;
            }
        }
        const rsi = this.currentDown !== 0 ? 100 - 100 / (1 + this.currentUp / this.currentDown) : 0;
        this.SetValue(0, 0, rsi);
    }
    CalcWMA() {
        const CurrentData = this.CurrentData;
        const N = this.RSIPeriod;
        if (CurrentData.Count === N + 1) {
            this.CalcSimple();
            return;
        }
        const diff = CurrentData.getPriceByType(this.SourcePrice, 0) - CurrentData.getPriceByType(this.SourcePrice, 1);
        let currentUp = 0;
        let currentDown = 0;
        if (diff >= 0) {
            currentUp = diff;
        }
        else {
            currentDown = Math.abs(diff);
        }
        let rsi = 100;
        const alpha = 1 / N;
        this.currentUp = alpha * currentUp + (1 - alpha) * this.previousUp;
        this.currentDown = alpha * currentDown + (1 - alpha) * this.previousDown;
        if (this.currentDown !== 0) {
            rsi = 100 - 100 / (this.currentUp / this.currentDown + 1);
        }
        this.SetValue(0, 0, rsi);
    }
}
